<script lang="ts" setup>
  import Loading from '@leaflink/stash/Loading.vue';
  import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
  import { computed, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';

  import { DefaultLayout, FullPageLayout } from '@/components/layout';
  import { LAYOUTS } from '@/constants';
  import { env } from '@/constants/env';

  type DefaultComponentInstance = InstanceType<typeof DefaultLayout>;
  type FullPageComponentInstance = InstanceType<typeof FullPageLayout>;
  type AppLayouts = FullPageComponentInstance | DefaultComponentInstance;

  const route = useRoute();

  const layouts = computed<Record<string, AppLayouts>>(() => ({
    [LAYOUTS.DEFAULT]: DefaultLayout,
    [LAYOUTS.FULL]: FullPageLayout,
  }));

  const currentLayout = ref();

  watch(
    () => route.path,
    (currentPath, previousPath) => {
      /**
       * Keep currentLayout as undefined in order to show the <Loading /> component until Vue Router has
       * pushed the current route to the history stack; the currentPath will be '/' until this has happened.
       * Django's router is configured to always redirect the '/' path to another non-empty path.
       */
      if (previousPath || (currentPath !== '/' && previousPath !== currentPath)) {
        const metaLayout = route.meta.layout;
        const isMetaLayoutValid = typeof metaLayout === 'string' && !!layouts.value[metaLayout];

        currentLayout.value = isMetaLayoutValid ? metaLayout : LAYOUTS.DEFAULT;
      }
    },
    { immediate: true },
  );
</script>

<template>
  <div v-if="!currentLayout" class="tw-min-h-screen tw-w-screen tw-grid tw-place-content-center">
    <!-- TODO: improve the initial loading experience -->
    <Loading data-test="layout-loader" />
  </div>

  <component v-else :is="layouts[currentLayout]" />

  <VueQueryDevtools v-if="env.VITE_ENABLE_VUE_QUERY_DEV_TOOLS" button-position="bottom-left" />
</template>
